import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { fetchArchivedAgents } from "@/services/templateService";
import { voiceAgentService } from "@/services/voiceAgentService";

export interface ArchivedAgent {
  id: string;
  title: string;
  agent_name?: string;
  description: string | null;
  organization_id: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  avatar_url: string | null;
  archived: boolean;
  type?: "chat" | "voice"; // Add type to distinguish between agent types
}

export const useArchivedAgents = () => {
  const [archivedAgents, setArchivedAgents] = useState<ArchivedAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organization } = useAuth();
  const { toast } = useToast();

  const fetchAgents = useCallback(async () => {
    if (!organization?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch archived chat agents
      const { data: chatAgents, error: chatError } = await fetchArchivedAgents(
        organization.id,
      );
      if (chatError) {
        throw chatError;
      }

      // Fetch archived voice agents
      const { data: voiceAgents, error: voiceError } =
        await voiceAgentService.getArchivedVoiceAgents();
      if (voiceError) {
        console.warn("Failed to fetch archived voice agents:", voiceError);
      }

      // Combine and mark agent types
      const allArchivedAgents: ArchivedAgent[] = [
        ...(chatAgents || []).map((agent: any) => ({
          ...agent,
          type: "chat" as const,
        })),
        ...(voiceAgents || []).map((agent: any) => ({
          ...agent,
          type: "voice" as const,
        })),
      ];

      // Sort by updated_at descending
      allArchivedAgents.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );

      setArchivedAgents(allArchivedAgents);
    } catch (err: any) {
      setError(err.message || "Failed to fetch archived agents");
      toast({
        variant: "destructive",
        title: "Error fetching archived agents",
        description: err.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id, toast]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  return { archivedAgents, isLoading, error, refetch: fetchAgents };
};
